/**
 * ComingUp.tsx · 09_APP · OS-006 · 2026-10-20 · C51, D-0086, v71
 *
 * Her words, 2026-10-20 ~20:10: "the calendar should definitely be connected and showing on like the todays to-do
 * list, like a meeting coming up, your women's empowerment outreach" and "the system should always ask, oh, you
 * just had your outreach today. How many women were in the presentation".
 *
 * Two pieces on Today:
 *  · OutreachAsk  · under the Ask bar. Shows only after an outreach on her calendar has ENDED and has no headcount.
 *                   One number, Save, done. "Not outreach" clears it for good. An estimate is ticked as one (C50).
 *  · ComingUp     · the next seven days from her calendar: title, time, place. Never a description (v71).
 *
 * Every write goes through an os_ verb and returns the live impact numbers, which the strip at the bottom picks up
 * through announceImpact(), so the number she just gave appears without a reload.
 */

import { useEffect, useState } from "react";
import { os, type CalendarItem, type Impact, type NetworkingDue, type OutreachDue } from "../lib/os";
import { openCardScanner } from "./ScanCard";

const TZ = "America/Los_Angeles";
const IMPACT_EVENT = "c2c:impact";

/** Tell the impact strip a number changed. */
export function announceImpact(impact: Impact) {
  window.dispatchEvent(new CustomEvent<Impact>(IMPACT_EVENT, { detail: impact }));
}
/** The impact strip listens with this. */
export function onImpact(fn: (impact: Impact) => void) {
  const h = (e: Event) => fn((e as CustomEvent<Impact>).detail);
  window.addEventListener(IMPACT_EVENT, h);
  return () => window.removeEventListener(IMPACT_EVENT, h);
}
/** Lets the Ask bar tell this card an outreach was logged by voice, so the ask disappears. */
const DUE_EVENT = "c2c:outreach-due";
export function announceDueChanged() { window.dispatchEvent(new Event(DUE_EVENT)); }

const dayKey = (iso: string) => new Date(iso).toLocaleDateString("en-CA", { timeZone: TZ });
const todayKey = () => new Date().toLocaleDateString("en-CA", { timeZone: TZ });
const time = (iso: string) => new Date(iso).toLocaleTimeString("en-US", { timeZone: TZ, hour: "numeric", minute: "2-digit" });

function dayLabel(key: string): string {
  const t = todayKey();
  const tomorrow = new Date(new Date(t + "T12:00:00").getTime() + 864e5).toLocaleDateString("en-CA");
  if (key === t) return "Today";
  if (key === tomorrow) return "Tomorrow";
  return new Date(key + "T12:00:00").toLocaleDateString("en-US", { weekday: "long", month: "short", day: "numeric" });
}

function whenSpoken(iso: string): string {
  const k = dayKey(iso);
  const label = dayLabel(k);
  return label === "Today" ? "today" : label === "Tomorrow" ? "tomorrow" : `on ${label}`;
}

// ---------------------------------------------------------------------------------------------------------------

export function OutreachAsk() {
  const [due, setDue] = useState<OutreachDue[]>([]);
  const load = () => { os.outreachDue().then(setDue).catch(() => { /* Today still loads */ }); };
  useEffect(() => {
    load();
    window.addEventListener(DUE_EVENT, load);
    return () => window.removeEventListener(DUE_EVENT, load);
  }, []);
  if (due.length === 0) return null;
  return (
    <section className="oask-wrap" aria-label="How many women were there">
      {due.map((d) => <OneAsk key={d.id} item={d} onDone={setDue} />)}
    </section>
  );
}

function OneAsk({ item, onDone }: { item: OutreachDue; onDone: (left: OutreachDue[]) => void }) {
  const [n, setN] = useState("");
  const [est, setEst] = useState(false);
  const [busy, setBusy] = useState(false);
  const [msg, setMsg] = useState<string | null>(null);
  const count = Number.parseInt(n, 10);
  const ok = Number.isFinite(count) && count >= 0;

  async function save() {
    if (!ok || busy) return;
    setBusy(true); setMsg(null);
    try {
      const impact = await os.outreachLog(item.place, count, dayKey(item.starts_at), "talk", est, item.id);
      announceImpact(impact);
      setMsg(`Saved: ${count} women at ${item.place}${est ? " (estimate)" : ""}. It is in Women reached.`);
      window.setTimeout(() => { os.outreachDue().then(onDone).catch(() => onDone([])); }, 2200);
    } catch (e) { setMsg(e instanceof Error ? e.message : String(e)); setBusy(false); }
  }

  async function notOutreach() {
    if (busy) return;
    setBusy(true);
    try { onDone(await os.outreachDismiss(item.id)); }
    catch (e) { setMsg(e instanceof Error ? e.message : String(e)); setBusy(false); }
  }

  return (
    <div className="oask">
      <p className="oask-q">You spoke at <b>{item.place}</b> {whenSpoken(item.starts_at)}.<br />How many women were in the room?</p>
      <p className="oask-sub">{item.title} · {time(item.starts_at)}</p>
      <div className="oask-row">
        <input className="ref-in" type="number" inputMode="numeric" min={0} value={n} autoComplete="off"
          onChange={(e) => setN(e.target.value)} onKeyDown={(e) => { if (e.key === "Enter") void save(); }}
          placeholder="Number of women" aria-label="Number of women" />
        <button className="ref-save" disabled={!ok || busy} onClick={() => void save()}>{busy && !msg ? "Saving" : "Save"}</button>
      </div>
      <label className="out-est"><input type="checkbox" checked={est} onChange={(e) => setEst(e.target.checked)} /> This number is an estimate, not counted in the room</label>
      <button className="oask-no" disabled={busy} onClick={() => void notOutreach()}>Not outreach, do not ask about this one</button>
      {msg && <p className="ref-ok" role="status">{msg}</p>}
    </div>
  );
}

// ---------------------------------------------------------------------------------------------------------------

const SHOW_FIRST = 6;

export function ComingUp() {
  const [items, setItems] = useState<CalendarItem[] | null>(null);
  const [err, setErr] = useState(false);
  const [all, setAll] = useState(false);
  useEffect(() => { os.comingUp(7).then(setItems).catch(() => setErr(true)); }, []);

  if (err) return null;                 // Today still loads; the card just stays away
  if (items === null) return null;
  const now = Date.now();
  const live = items.filter((c) => new Date(c.ends_at ?? c.starts_at).getTime() > now);
  const shown = all ? live : live.slice(0, SHOW_FIRST);

  const groups: Array<{ key: string; rows: CalendarItem[] }> = [];
  for (const c of shown) {
    const k = dayKey(c.starts_at);
    const g = groups[groups.length - 1];
    if (g && g.key === k) g.rows.push(c); else groups.push({ key: k, rows: [c] });
  }

  return (
    <section className="cup">
      <h2 className="sec">Coming up <span>from your calendar, the next 7 days</span></h2>
      {live.length === 0 ? <p className="cup-empty">Nothing on your calendar for the next 7 days.</p> : (
        <div className="cup-card">
          {groups.map((g) => (
            <div key={g.key} className="cup-day">
              <h3>{dayLabel(g.key)}</h3>
              <ul>
                {g.rows.map((c) => (
                  <li key={c.id} className={c.is_outreach ? "is-out" : undefined}>
                    <span className="cup-t">{c.all_day ? "All day" : time(c.starts_at)}</span>
                    <span className="cup-w"><b>{c.title}</b>
                      {c.location && <small>{c.location}</small>}
                      {c.is_outreach && <em>Outreach · I will ask how many women were there</em>}
                      {c.is_networking && <em className="net">Networking · I will ask if you got any cards</em>}
                      <button className={c.is_networking ? "cup-net on" : "cup-net"} aria-pressed={!!c.is_networking}
                        onClick={() => void os.calendarMark(c.id, !c.is_networking).then(setItems).catch(() => { /* keep */ })}>
                        {c.is_networking ? "✓ Networking" : "Networking?"}
                      </button>
                    </span>
                  </li>
                ))}
              </ul>
            </div>
          ))}
          {live.length > SHOW_FIRST && (
            <button className="cup-more" onClick={() => setAll((a) => !a)}>
              {all ? "Show less" : `Show all ${live.length}`}
            </button>
          )}
        </div>
      )}
    </section>
  );
}

// ---------------------------------------------------------------------------------------------------------------
// C54 · v72 · her words, 2026-10-20 ~23:58: "if I go to an event where there might be networking and stuff, there
// should be a reminder like, did you get any cards that we should scan into the system right now? Things like that."

export function NetworkingAsk() {
  const [due, setDue] = useState<NetworkingDue[]>([]);
  useEffect(() => { os.networkingDue().then(setDue).catch(() => { /* Today still loads */ }); }, []);
  if (due.length === 0) return null;
  return (
    <section className="oask-wrap" aria-label="Did you get any cards">
      {due.map((d) => <OneNetworking key={d.id} item={d} onDone={setDue} />)}
    </section>
  );
}

function OneNetworking({ item, onDone }: { item: NetworkingDue; onDone: (left: NetworkingDue[]) => void }) {
  const [step, setStep] = useState<"ask" | "scanning" | "follow">("ask");
  const [answer, setAnswer] = useState<"scanned" | "no_cards">("no_cards");
  const [follow, setFollow] = useState("");
  const [busy, setBusy] = useState(false);
  const [msg, setMsg] = useState<string | null>(null);

  async function finish(a: "scanned" | "no_cards" | "not_networking", f: string | null) {
    if (busy) return;
    setBusy(true); setMsg(null);
    try { onDone(await os.networkingAnswer(item.id, a, f)); }
    catch (e) { setMsg(e instanceof Error ? e.message : String(e)); setBusy(false); }
  }

  return (
    <div className="oask">
      {step === "ask" && <>
        <p className="oask-q">You were at <b>{item.title}</b> {whenSpoken(item.starts_at)}.<br />Did you get any business cards?</p>
        <div className="askc-offer-b">
          <button className="ref-save" onClick={() => { setAnswer("scanned"); setStep("scanning"); openCardScanner(item.title); }}>Scan cards</button>
          <button className="oask-ghost" onClick={() => { setAnswer("no_cards"); setStep("follow"); }}>No cards</button>
        </div>
        <button className="oask-no" disabled={busy} onClick={() => void finish("not_networking", null)}>Not a networking event</button>
      </>}
      {step === "scanning" && <>
        <p className="oask-q">Scanning cards from <b>{item.title}</b>.</p>
        <p className="oask-sub">Each card you scan now is saved with this event, so you will always know where you met them.</p>
        <div className="askc-offer-b">
          <button className="oask-ghost" onClick={() => openCardScanner(item.title)}>Scan another card</button>
          <button className="ref-save" onClick={() => setStep("follow")}>Done scanning</button>
        </div>
      </>}
      {step === "follow" && <>
        <p className="oask-q">Anyone to follow up with from <b>{item.title}</b>?</p>
        <input className="ref-in" value={follow} onChange={(e) => setFollow(e.target.value)} placeholder="Who, and what about (optional)"
          onKeyDown={(e) => { if (e.key === "Enter") void finish(answer, follow); }} />
        <div className="askc-offer-b">
          <button className="ref-save" disabled={busy} onClick={() => void finish(answer, follow)}>{follow.trim() ? "Save follow-up" : "Done"}</button>
        </div>
        <p className="oask-sub">A follow-up goes on Today under Next steps.</p>
      </>}
      {msg && <p className="ref-ok" role="status">{msg}</p>}
    </div>
  );
}
